import {
  type ComponentProps,
  type HTMLAttributes,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'

type CodeBlockContextType = {
  code: string
}

const CodeBlockContext = createContext<CodeBlockContextType>({ code: '' })

export type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string
  language?: string
  showLineNumbers?: boolean
}

export function CodeBlock({
  code,
  language: _language,
  showLineNumbers = false,
  className,
  children,
  ...props
}: CodeBlockProps) {
  const lines = showLineNumbers ? code.split('\n') : null

  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          'group relative w-full overflow-hidden rounded-md border bg-background text-foreground',
          className
        )}
        {...props}
      >
        <div className='relative'>
          <pre className='m-0 overflow-auto p-4 font-mono text-[12px] leading-relaxed whitespace-pre-wrap wrap-break-word text-foreground'>
            {lines ? (
              <code>
                {lines.map((line, idx) => (
                  <span key={idx} className='flex'>
                    <span className='mr-4 inline-block min-w-10 text-right text-muted-foreground select-none'>
                      {idx + 1}
                    </span>
                    <span>{line}</span>
                  </span>
                ))}
              </code>
            ) : (
              <code>{code}</code>
            )}
          </pre>
          {children ? (
            <div className='absolute inset-e-2 top-2 flex items-center gap-2'>
              {children}
            </div>
          ) : null}
        </div>
      </div>
    </CodeBlockContext.Provider>
  )
}

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void
  onError?: (error: Error) => void
  timeout?: number
  copyIcon?: ReactNode
  copiedIcon?: ReactNode
}

export function CodeBlockCopyButton({
  onCopy,
  onError,
  timeout = 2000,
  className,
  children,
  copyIcon,
  copiedIcon,
  ...props
}: CodeBlockCopyButtonProps) {
  const [isCopied, setIsCopied] = useState(false)
  const { code } = useContext(CodeBlockContext)

  const copyToClipboard = useCallback(async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not available'))
      return
    }
    try {
      await navigator.clipboard.writeText(code)
      setIsCopied(true)
      onCopy?.()
      window.setTimeout(() => setIsCopied(false), timeout)
    } catch (error) {
      onError?.(error as Error)
    }
  }, [code, onCopy, onError, timeout])

  const defaultIcon = isCopied ? (
    copiedIcon ?? <CheckIcon size={14} />
  ) : (
    copyIcon ?? <CopyIcon size={14} />
  )

  return (
    <Button
      type='button'
      className={cn('shrink-0', className)}
      onClick={copyToClipboard}
      size='icon'
      variant='ghost'
      {...props}
    >
      {children ?? defaultIcon}
    </Button>
  )
}
